import React, {
  useMemo, useEffect, useRef, useCallback,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  string, array, func, bool, object, element, oneOfType,
} from 'prop-types';
import Tagify from '@yaireo/tagify';
import '@yaireo/tagify/dist/tagify.css';

const noop = (_) => _;

const isSameDeep = (a, b) => {
  const trans = (x) => (typeof x === 'string' ? x : JSON.stringify(x));
  return trans(a) == trans(b);
};

// if a template is a React component, it should be outputed as a String (and not as a React component)
function templatesToString(templates) {
  if (templates) {
    for (const templateName in templates) {
      const Template = templates[templateName];
      const isReactComp = String(Template).includes('jsxRuntime');
      if (isReactComp) {
        templates[templateName] = (...props) => (console.log({ props }), renderToStaticMarkup(<Template props={props} />));
      }
    }
  }
}

const TagifyWrapper = ({
  name,
  value,
  loading = false,
  onChange = noop,
  readOnly,
  disabled,
  children,
  settings = {},
  InputMode = 'input',
  autoFocus,
  className,
  whitelist,
  tagifyRef,
  placeholder = '',
  defaultValue,
  showDropdown,
  inputElmRef,
}) => {
  const mountedRef = useRef();
  const tagify = useRef();
  const _value = defaultValue || value;

  const inputAttrs = useMemo(() => ({
    ref: inputElmRef,
    name,
    defaultValue: children || typeof _value === 'string' ? _value : JSON.stringify(_value),
    className,
    readOnly,
    disabled,
    autoFocus,
    placeholder,
  }), []);

  const setFocus = useCallback(() => {
    autoFocus && tagify.current && tagify.current.DOM.input.focus();
  }, [tagify]);

  useEffect(() => {
    templatesToString(settings.templates);

    if (InputMode == 'textarea') settings.mode = 'mix';

    // "whitelist" prop takes precedence
    if (whitelist && whitelist.length) settings.whitelist = whitelist;

    const t = new Tagify(inputElmRef.current, settings);

    t.on('change', onChange)

    // Bridge Tagify instance with parent component
    if (tagifyRef) {
      tagifyRef.current = t;
    }

    tagify.current = t;

    setFocus();

    // cleanup
    return () => {
      t.destroy();
    };
  }, []);

  useEffect(() => {
    setFocus();
  }, [autoFocus]);

  useEffect(() => {
    if (mountedRef.current) {
      tagify.current.settings.whitelist.length = 0;

      // replace whitelist array items
      whitelist && whitelist.length && tagify.current.settings.whitelist.push(...whitelist);
    }
  }, [whitelist]);

  useEffect(() => {
    const currentValue = tagify.current.getInputValue();

    if (mountedRef.current && !isSameDeep(value, currentValue)) {
      tagify.current.loadOriginalValues(value);
    }
  }, [value]);

  useEffect(() => {
    if (mountedRef.current) {
      tagify.current.toggleClass(className);
    }
  }, [className]);

  useEffect(() => {
    if (mountedRef.current) {
      tagify.current.loading(loading);
    }
  }, [loading]);

  useEffect(() => {
    if (mountedRef.current) {
      tagify.current.setReadonly(readOnly || disabled);
    }
  }, [readOnly, disabled]);

  useEffect(() => {
    const t = tagify.current;

    if (mountedRef.current) {
      if (showDropdown) {
        t.dropdown.show.call(t, showDropdown);
        t.toggleFocusClass(true);
      } else {
        t.dropdown.hide.call(t);
      }
    }
  }, [showDropdown]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  return (
  // a wrapper must be used because Tagify will appened inside it it's component,
  // keeping the virtual-DOM out of the way
    <div className="tags-input">
      <InputMode {...inputAttrs} />
    </div>
  );
};

TagifyWrapper.propTypes = {
  name: string,
  value: oneOfType([string, array]),
  loading: bool,
  children: oneOfType([string, array]),
  onChange: func,
  readOnly: bool,
  settings: object,
  InputMode: string,
  autoFocus: bool,
  className: string,
  tagifyRef: object,
  whitelist: array,
  placeholder: string,
  defaultValue: oneOfType([string, array]),
  showDropdown: oneOfType([string, bool]),
  onInput: func,
  onAdd: func,
  onRemove: func,
  onEditInput: func,
  onEditBeforeUpdate: func,
  onEditUpdated: func,
  onEditStart: func,
  onEditKeydown: func,
  onInvalid: func,
  onClick: func,
  onKeydown: func,
  onFocus: func,
  onBlur: func,
  onDropdownShow: func,
  onDropdownHide: func,
  onDropdownSelect: func,
  onDropdownScroll: func,
  onDropdownNoMatch: func,
  onDropdownUpdated: func,
};

const Tags = React.memo(TagifyWrapper);
Tags.displayName = 'Tags';

export const MixedTags = ({ children, ...rest }) => <Tags InputMode="textarea" {...rest}>{children}</Tags>;

export default Tags;
